import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Customer } from './CustomerSelectionView';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

interface CombineCustomerDialogProps {
  source: Customer;
  onClose: () => void;
  onMerged: () => void;
}

const displayName = (c: { name: string; company?: string; lineType?: string }) =>
  c.lineType === 'personal' ? c.name : (c.company || c.name);

/**
 * Merge the `source` customer INTO a target customer the user picks. All of the
 * source's folders, documents, reports, proposals, summaries, chats and workflows
 * move to the target; the source customer is then deleted.
 */
const CombineCustomerDialog: React.FC<CombineCustomerDialogProps> = ({ source, onClose, onMerged }) => {
  const { token } = useAuth();
  const [candidates, setCandidates] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/customers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load customers');
        const data = await res.json();
        const list: Customer[] = Array.isArray(data) ? data : (data.customers || []);
        // Only customers the current user owns (merge requires owning both) and not the source itself
        setCandidates(list.filter((c) => c.id !== source.id && c.isOwner !== false));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, source.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      displayName(c).toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [candidates, search]);

  const handleMerge = async () => {
    if (!targetId) return;
    const target = candidates.find((c) => c.id === targetId);
    if (!target) return;
    if (!window.confirm(
      `Merge "${displayName(source)}" into "${displayName(target)}"?\n\n` +
      `All folders, documents, reports, proposals, summaries, chats and workflows from ` +
      `"${displayName(source)}" will move into "${displayName(target)}", and ` +
      `"${displayName(source)}" will be permanently deleted. This cannot be undone.`
    )) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/${targetId}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sourceCustomerId: source.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Merge failed');
      }
      onMerged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-secondary dark:text-dark-text">
            Combine Customer
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Merge <span className="font-medium">{displayName(source)}</span> into another customer.
          Everything in it moves to the target, then this customer is deleted.
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers to merge into…"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white mb-3 focus:ring-2 focus:ring-primary"
        />

        <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
              No other customers you own to merge into.
            </div>
          ) : (
            filtered.map((c) => (
              <label
                key={c.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${targetId === c.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
              >
                <input
                  type="radio"
                  name="merge-target"
                  checked={targetId === c.id}
                  onChange={() => setTargetId(c.id)}
                  className="text-primary focus:ring-primary"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-secondary dark:text-dark-text truncate">{displayName(c)}</div>
                  {c.email && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.email}</div>}
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={!targetId || busy}
            className="flex-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {busy ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CombineCustomerDialog;
